import { useCallback, useMemo } from 'react';
import { useRouter } from 'next/router';
import { signOut, useSession } from '@ghq-abi/auth-client-lib/client';
import { useTranslate } from '@tolgee/react';
import nprogress from 'nprogress';

import { User } from '~/shared/auth/useUser';
import { ProxyIcon } from '~/shared/components/icons/Proxy';
import { SighOut } from '~/shared/components/icons/SignOut';
import { StopProxy } from '~/shared/components/icons/StopProxy';
import { useGetEmployeeRole } from '~/entities/RoleSetting/api/useGetEmployeeRole';

type UseUserDropdownProps = {
  user: User;
};

export const useUserDropdown = ({ user }: UseUserDropdownProps) => {
  const { t } = useTranslate();
  const { data: session, proxyOut } = useSession();
  const router = useRouter();
  const { data: employeeRole } = useGetEmployeeRole(
    session?.user.employeeId || ''
  );

  const isProxying = !!session?.user.proxiedAs;

  async function handleSignOut() {
    try {
      await fetch('/api/destroy-language-cookie');
    } finally {
      signOut({ redirect: false });
    }
  }

  const goToProxy = useCallback(async () => {
    router.push('/proxy');
  }, [router]);

  const handleStopProxy = useCallback(async () => {
    if (!session) {
      return;
    }

    nprogress.start();
    try {
      await proxyOut();
    } finally {
      nprogress.done();
    }
  }, [session, proxyOut]);

  const items = useMemo(() => {
    const dropdownItems = [
      {
        label: t('common.sign_out'),
        onSelect: handleSignOut,
        icon: <SighOut />,
      },
    ];

    if (employeeRole?.role === 'GlobalAdmin') {
      dropdownItems.unshift({
        label: t(isProxying ? 'common.stop_proxy' : 'common.proxy_as_user'),
        onSelect: isProxying ? handleStopProxy : goToProxy,
        icon: isProxying ? <StopProxy size={18} /> : <ProxyIcon size={18} />,
      });
    }

    return dropdownItems;
  }, [goToProxy, handleStopProxy, isProxying, t, employeeRole?.role]);

  const profileInfo = useMemo(
    () => ({
      name: user.name ?? '',
      urlImage:
        process.env.NEXT_PUBLIC_PEOPLE_PLATFORM_PHOTO_API_URL && user.globalId
          ? `${process.env.NEXT_PUBLIC_PEOPLE_PLATFORM_PHOTO_API_URL}/${user.globalId}`
          : undefined,
      zone: user.zone ?? '',
    }),
    [user.globalId, user.name, user.zone]
  );

  return {
    items,
    profileInfo,
  };
};
